import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import moment from 'moment-timezone';
import { guid } from '@/mixins/guid';
import { modals } from '@/mixins/modal';
import { queuedWork } from '@/mixins/queued-work';
import { siteBuilder } from '@/mixins/site-builder';
import { recycleDroplet } from '@/mixins/recycle-droplet';
import droplets from '@editor/droplets/droplets.config.json';
import textInput from './inputs/text';
import addressInput from './inputs/address';
import uploadInput from './inputs/upload';
import galleryInput from './inputs/gallery';
import textareaInput from './inputs/textarea';
import checkboxInput from './inputs/checkbox';
import numberInput from './inputs/number';
import customOptionsInput from './inputs/custom-options';
import customLimitsInput from './inputs/custom-limits';
import wysiwygInput from './inputs/wysiwyg';
import multiselectInput from './inputs/multi-select';
import pageselectInput from './inputs/page-select';
import datepickerInput from './inputs/date-picker';
import socialMediaInput from './inputs/social-media';
import mediaInput from './inputs/media';
import externalMediaInput from './inputs/external-media';

export default Vue.component('edit-item', {
  template: require('./template.html'),
  name: 'edit-item',
  mixins: [
    guid,
    modals,
    siteBuilder,
    recycleDroplet,
    queuedWork,
  ],
  computed: {
    ...mapGetters([
      'currentSite',
      'currentPage',
      'activeSiteType',
    ]),
    savedPages() {
      return _.map(this.currentSite.pages, 'name');
    },
    typeData() {
      return this.currentSite.dynamic[this.activeSiteType];
    },
    inputErrorsMessage() {
      return this.inputErrors.map(er => `${this._startCase(er.key)} [${er.tab}]`).join(', ');
    },
    inputs() {
      return {
        text: textInput,
        number: numberInput,
        customOptions: customOptionsInput,
        customLimits: customLimitsInput,
        upload: uploadInput,
        gallery: galleryInput,
        textarea: textareaInput,
        checkbox: checkboxInput,
        wysiwyg: wysiwygInput,
        multiselect: multiselectInput,
        pageselect: pageselectInput,
        datepicker: datepickerInput,
        address: addressInput,
        socialmedia: socialMediaInput,
        media: mediaInput,
        externalmedia: externalMediaInput,
      };
    },
  },
  props: [
    'item-modal',
    'content',
    'active-item',
    'item-list',
    'all-categories',
    'dynamic-data',
    'auto-save-loader',
    'auto-save-success',
  ],
  data() {
    return {
      autoSave: null,
      saveErrors: false,
      deleteErrors: false,
      confirmDeleteItem: false,
      confirmArchive: false,
      innerTabs: {},
      openCropper: false,
      siteData: {},
      newFormDroplets: [],
      initialItem: {},
      showInputErrors: false,
      inputErrors: [],
      activeInput: false,
    };
  },
  created() {
    // dynamically set type data tab
    _.forEach(this.dynamicData.tabs, (tab) => {
      this.$set(this.innerTabs, tab, false);
    });
  },
  watch: {
    content(name) {
      if (name === 'edit-item') {
        this.setAutoSave();
        setTimeout(() => {
          this.setInnerTab(this.dynamicData.tabs[0]);
          const $firstTab = document.getElementById(`firstTab-${this.activeSiteType}`);
          if (this.activeItem && $firstTab) {
            $firstTab.click();
          }
        }, 200);
      } else {
        clearInterval(this.autoSave);
        _.forOwn(this.innerTabs, (value, key) => {
          this.innerTabs[key] = false;
        });
        this.setInitialItem({});
      }
    },
    activeItem(item) {
      if (item) {
        this.setInitialItem(item);
        this.setInitialValues();
        this.$log.info('Editing item: ', this.activeItem);
      }
    },
  },
  methods: {
    setInitialValues() {
      this.saveErrors = false;
      this.deleteErrors = false;
      this.confirmDeleteItem = false;
      this.confirmArchive = false;
      this.inputErrors = [];
      this.showInputErrors = false;
    },
    getInputs(tab) {
      return _.pickBy(this.dynamicData.itemData, item => item.tab === tab);
    },
    setInitialItem(item) {
      this.initialItem = _.cloneDeep(item);
    },
    async setAutoSave() {
      const autoSetting = this.currentSite.autosave;
      if (typeof autoSetting === 'undefined' || autoSetting) {
        this.autoSave = setInterval(async () => {
          this.checkAllRequired('auto');
          if (this.inputErrors.length > 0) {
            return;
          }
          if (!_.isEqual(this.initialItem, this.activeItem)) {
            try {
              this.$emit('set-auto-save-prop', { key: 'autoSaveLoader', val: true });
              await this.$lightning([{
                name: 'updateSiteAttr',
                payload: {
                  siteId: this.currentSite._id,
                  attr: 'dynamic',
                  value: this.currentSite.dynamic,
                },
              }]);
              this.setInitialItem(this.activeItem);
            } catch (e) {
              this.$log.error(e.message || e);
            } finally {
              this.$emit('set-auto-save-prop', { key: 'autoSaveLoader', val: false });
              this.$emit('set-auto-save-prop', { key: 'autoSaveSuccess', val: 'Saved!' });
              setTimeout(() => {
                this.$emit('set-auto-save-prop', { key: 'autoSaveSuccess', val: false });
              }, 2000);
            }
          }
        }, 10000);
      }
    },
    async archiveItem() {
      this.$emit('set-loading', true);
      this.saveErrors = false;
      this.deleteErrors = false;
      this.confirmArchive = false;
      try {
        await this.runPageChecks();
        // set pageId to null
        this.$store.dispatch('setDropletVal', {
          drp: this.activeItem,
          key: 'pageId',
          val: null,
        });
        // mark item as archived if not already
        this.$store.dispatch('setDropletVal', {
          drp: this.activeItem,
          key: 'status',
          val: 'archived',
        });
        // remove item from published list
        const index = _.findIndex(this.typeData.published, p => p._id === this.activeItem._id);
        if (index >= 0) {
          this.$store.dispatch('spliceDropletVal', {
            array: this.typeData.published,
            index,
          });
          this.$store.dispatch('setPublishItem', {
            type: this.activeSiteType,
            stamp: new Date().toISOString(),
          });
        }
        await this.$lightning([{
          name: 'updateSiteAttr',
          payload: {
            siteId: this.currentSite._id,
            attr: 'dynamic',
            value: this.currentSite.dynamic,
          },
        }]);
        // we also need to regenerate the site in this instance
        await this.callBuilder({
          publish: 'site',
          site: {
            _id: this.currentSite._id,
          },
        });
        this.$log.debug('successfully archived item');
        this.$emit('set-active-item', null);
        this.$emit('set-content', null);
        this.$store.dispatch('addNotification', {
          id: this.guid(),
          type: 'success',
          time: 2000,
          message: 'Item archived successfully!',
        });
      } catch (e) {
        this.$log.error(e);
        this.saveErrors = e.message || e;
        await this._reportError({
          error: e,
          fn: 'archiveItem',
        });
      } finally {
        this.$emit('set-loading', false);
      }
    },
    async publishItem() {
      this.checkAllRequired();
      if (this.inputErrors.length > 0) {
        this.showInputErrors = true;
        return;
      }
      this.$emit('set-loading', true);
      this.saveErrors = false;
      this.deleteErrors = false;
      try {
        // mark item as published if not already
        if (!this.activeItem.status !== 'published') {
          this.$store.dispatch('setDropletVal', {
            drp: this.activeItem,
            key: 'status',
            val: 'published',
          });
        }
        const timeStamp = new Date().toISOString();
        // this should only be marked the first time this item is published
        if (!this.activeItem.publishedOn) {
          this.$store.dispatch('setDropletVal', {
            drp: this.activeItem,
            key: 'publishedOn',
            val: timeStamp,
          });
        }
        // change 'updated' time stamp
        this.$store.dispatch('setDropletVal', {
          drp: this.activeItem,
          key: 'updated',
          val: timeStamp,
        });
        const functions = [];
        // this new page generation should only run once. if it hasn't been built already
        if (this.typeData.page
            && !this.typeData.page.modal
            && !this.typeData.page.external
            && this.typeData.page.dynamicId) {
          const pageCreated = _.find(this.currentSite.pages, p => p._id === this.activeItem.pageId);
          const dynamicPage = _.find(
            this.typeData.pages || [], p => p._id === this.typeData.page.dynamicId,
          );
          if (!pageCreated && dynamicPage) {
            // we need to do a name check because if there is another page with the same
            // name that already exists, this will cause problems in s3 as there can't be
            // 2 folders with the same name
            const newPage = await this.generateFromDynamic(dynamicPage);
            const item = this.lowerCase(this.dynamicData.itemLabel);
            // name check here
            const limits = this._checkLimits(this.siteData);
            if (!limits.pages.reached && !limits.forms.reached) {
              this.$store.dispatch('addPage', newPage);
              this.$store.dispatch('addToSiteMap', newPage);
              functions.push({
                name: 'addPageItem',
                payload: {
                  siteId: this.currentSite._id,
                  siteMap: this.currentSite.siteMap,
                  item: newPage,
                },
              });
              // link new page id to item
              this.$store.dispatch('setDropletVal', {
                drp: this.activeItem,
                key: 'pageId',
                val: newPage._id,
              });
              // also add pageLink to droplet
              this.$store.dispatch('setDropletVal', {
                drp: this.activeItem,
                key: 'pageLink',
                val: newPage._id,
              });
              // add actual limit counts to overall site totals
              _.forEach(this.newFormDroplets, (drp) => {
                this.$store.dispatch('addFormCount', { drp });
              });
            }
            // if user has reached certain limits based on tier, don't create new page but
            // also don't stop the publishing of this item
            if (limits.pages.reached) {
              this.$store.dispatch('addNotification', {
                id: this.guid(),
                type: 'warning',
                message: `This site has reached the page limit for this plan, therefore no new page was generated for this ${item}.`,
              });
            }
            if (limits.forms.reached) {
              this.$store.dispatch('addNotification', {
                id: this.guid(),
                type: 'warning',
                message: `This site has reached the form limit and the dynamic page tied to these ${item}s contains 1 or more forms, therefore no new page was generated for this ${item}.`,
              });
            }
          }
          // if published already, we need to update page meta information
          if (pageCreated) {
            const title = this.activeItem[this.dynamicData.page.title];
            this.$store.dispatch('setGlobalValue', {
              item: pageCreated,
              key: 'name',
              value: title,
            });
            this.$store.dispatch('setGlobalValue', {
              item: pageCreated,
              key: 'title',
              value: `${this.currentSite.alias} | ${title}`,
            });
            let description = this.getPrefixes(this.dynamicData.page.descriptionPrefixes);
            const dynamicDesc = this.activeItem[this.dynamicData.page.description];
            if (dynamicDesc) {
              const prefix = !description ? '' : ' | ';
              description = `${description}${prefix}${dynamicDesc}`;
            }
            description = description ? this.truncate(description, 160) : description;
            this.$store.dispatch('setGlobalValue', {
              item: pageCreated,
              key: 'description',
              value: description,
            });
            const metaImage = this.activeItem[this.dynamicData.page.metaImage];
            this.$store.dispatch('setGlobalValue', {
              item: pageCreated.meta,
              key: 'image',
              value: metaImage,
            });
            this.$store.dispatch('setGlobalValue', {
              item: pageCreated,
              key: 'status',
              value: 'published',
            });
            const updates = [
              { key: 'name', value: title },
              { key: 'title', value: `${this.currentSite.alias} | ${title}` },
              { key: 'description', value: description },
              { key: 'meta', value: pageCreated.meta },
              { key: 'status', value: 'published' },
            ];
            if (typeof pageCreated.defaultDescription === 'undefined') {
              updates.push({ key: 'defaultDescription', value: false });
              this.$store.dispatch('setGlobalValue', {
                item: pageCreated,
                key: 'defaultDescription',
                value: false,
              });
            }
            // pageCreated needs some kind of diff
            // save to dynamo
            await this.$lightning([{
              name: 'updatePageAttr',
              payload: {
                siteId: this.currentSite._id,
                pageId: pageCreated._id,
                updates,
              },
            }]);
          }
        }
        // copy to published
        const published = _.cloneDeep(this.activeItem);
        const data = this.currentSite.dynamic[this.activeSiteType];
        if (!data.published) {
          this.$store.dispatch('setDropletVal', {
            drp: this.currentSite.dynamic[this.activeSiteType],
            key: 'published',
            val: [published],
          });
        }
        const index = _.findIndex(data.published, i => i._id === published._id);
        if (index >= 0) {
          this.$store.dispatch('replaceDropletVal', {
            array: data.published,
            index,
            val: published,
          });
        } else {
          this.$store.dispatch('pushDropletVal', {
            array: data.published,
            val: published,
          });
          // run sort of list
          let list = _.cloneDeep(data.published);
          list = list.sort((a, b) => new Date(b.created) - new Date(a.created));
          this.$store.dispatch('setDropletVal', {
            drp: this.currentSite.dynamic[this.activeSiteType],
            key: 'published',
            val: list,
          });
        }
        // save to db
        functions.push({
          name: 'updateSiteAttr',
          payload: {
            siteId: this.currentSite._id,
            attr: 'dynamic',
            value: this.currentSite.dynamic,
          },
        });
        await this.$lightning(functions);
        // regenerate site
        // TODO: this would be removed after the droplets api fetches data
        await this.callBuilder({
          publish: 'site',
          site: {
            _id: this.currentSite._id,
          },
        });
        this.$store.dispatch('setPublishItem', {
          type: this.activeSiteType,
          stamp: timeStamp,
        });
        this.setInitialItem(this.activeItem);
        this.$log.debug('successfully published item');
        this.$emit('set-active-item', null);
        this.$emit('set-content', null);
        this.$store.dispatch('addNotification', {
          id: this.guid(),
          type: 'success',
          time: 2000,
          message: 'Item published successfully!',
        });
      } catch (e) {
        this.$log.error(e);
        this.saveErrors = e.message || e;
        await this._reportError({
          error: e,
          fn: 'publishItem',
        });
      } finally {
        this.$emit('set-loading', false);
      }
    },
    makeURLStr(str = '') {
      const url = str.replace(/[^a-zA-Z0-9]/g, '-');
      return _.chain(url)
        .toLower()
        .replace(/ /g, '-')
        .value();
    },
    makePageName(title) {
      let name = title;
      if (this.savedPages.includes(name)) {
        const regex = new RegExp(`^${name} \\d+$|^${name}$`, 'i');
        const dupes = this.savedPages.filter(name => name.match(regex));
        const numbers = dupes.map(name => parseInt(name.split(title)[1] || 0, 10));
        const max = Math.max(...numbers) + 1;
        const next = max === 1 ? 2 : max;
        name = `${name} ${next}`;
      }
      return name;
    },
    getPrefixes(list) {
      if (!list) {
        return false;
      }
      const item = this.activeItem;
      const vals = list.filter(key => this._getKeyVal(item, key)).map((key) => {
        let val = this._getKeyVal(item, key);
        if (moment(val, moment.ISO_8601, true).isValid()) {
          val = this._zoneFormat({
            date: val,
            zone: this.currentSite.timezone,
          });
        }
        return val;
      });
      return vals.join(' | ');
    },
    async generateFromDynamic(dynamicPage) {
      const dynamicItem = this.activeItem;
      const page = _.cloneDeep(dynamicPage);
      page._id = this.guid();
      page.createdDate = new Date().toISOString();
      page.type = 'page';
      page.dynamicItemId = dynamicItem._id;
      page.status = 'published';
      // auto set meta information
      if (dynamicItem[this.dynamicData.page.title]) {
        page.defaultTitle = false;
        page.title = `${this.currentSite.alias} | ${dynamicItem[this.dynamicData.page.title]}`;
      }
      let description = this.getPrefixes(this.dynamicData.page.descriptionPrefixes);
      const dynamicDesc = dynamicItem[this.dynamicData.page.description];
      if (dynamicDesc) {
        page.defaultDescription = false;
        const prefix = !description ? '' : ' | ';
        description = `${description}${prefix}${dynamicDesc}`;
      }
      page.description = description ? this.truncate(description, 160) : description;
      if (dynamicItem[this.dynamicData.page.metaImage]) {
        page.defaultMeta = false;
        page.meta.image = dynamicItem[this.dynamicData.page.metaImage];
      }
      const title = dynamicItem[this.dynamicData.page.title] || `Untitled-${Date.now()}`;
      page.name = this.makePageName(title);
      page.url = `${this.makeURLStr(page.name)}/`;
      // setup site data to check limits
      this.siteData.tier = this.currentSite.tier;
      this.siteData.pages = _.clone(this.currentSite.pages);
      this.siteData.pages.push(page);
      this.siteData.drafts = _.clone(this.currentSite.drafts);
      this.siteData.templates = _.clone(this.currentSite.templates);
      this.siteData.forms = _.clone(this.currentSite.forms);
      this.siteData.recycled = _.clone(this.currentSite.recycled);
      this.newFormDroplets = [];
      page.blocks = await Promise.all(page.blocks.map(droplet => new Promise(async (res) => {
        if (droplet.templateRegion) {
          res(droplet);
        } else {
          const newId = this.guid();
          this.updateStyleString({
            oldId: droplet._id,
            newId,
            droplet,
          });
          droplet._id = newId;
          await this.routeRecycleAction({
            action: 'saveNewRecycled',
            newId,
            type: this._dropletType(droplet),
            droplet,
          });
          // we need to find droplets in each block that need to link to this dynamic data
          // they should in 99.9% of the time.
          await this._deepFind(
            {
              entry: [droplet],
              key: 'type',
            }, async (item) => {
              if (item._id) {
                const type = this._dropletType(item);
                // check if droplet found should be set to active dynamic item
                if (type.any && type.name) {
                  const config = droplets[type.name];
                  const link = config.linkDynamicItem;
                  if (link && (link.type === this.activeSiteType || link.type === 'all')) {
                    item[link.key] = dynamicItem._id;
                  }
                }
                if (type.any && type.name === 'form') {
                  this.siteData.forms.push(item._id);
                  this.newFormDroplets.push(item);
                }
              }
            },
          );
          res(droplet);
        }
      })));
      return page;
    },
    truncate(text, limit) {
      const description = text || '';
      if (description.length < limit) {
        return description;
      }
      return `${description.substring(0, limit)}...`;
    },
    async saveItem() {
      this.checkAllRequired();
      if (this.inputErrors.length > 0) {
        this.showInputErrors = true;
        return;
      }
      this.$emit('set-loading', true);
      this.saveErrors = false;
      this.deleteErrors = false;
      try {
        // save to db
        await this.$lightning([{
          name: 'updateSiteAttr',
          payload: {
            siteId: this.currentSite._id,
            attr: 'dynamic',
            value: this.currentSite.dynamic,
          },
        }]);
        this.setInitialItem(this.activeItem);
        this.$log.debug('successfully saved item');
        this.$store.dispatch('addNotification', {
          id: this.guid(),
          type: 'success',
          time: 2000,
          message: 'Item saved successfully!',
        });
      } catch (e) {
        this.$log.error(e);
        this.saveErrors = e.message || e;
        await this._reportError({
          error: e,
          fn: 'saveItem',
        });
      } finally {
        this.$emit('set-loading', false);
      }
    },
    async deleteItem() {
      this.$emit('set-loading', true);
      this.saveErrors = false;
      this.deleteErrors = false;
      this.confirmDeleteItem = false;
      try {
        await this.runPageChecks();
        // remove from state first
        // set pageId to null
        this.$store.dispatch('setDropletVal', {
          drp: this.activeItem,
          key: 'pageId',
          val: null,
        });
        // mark item as archived if not already
        this.$store.dispatch('setDropletVal', {
          drp: this.activeItem,
          key: 'status',
          val: 'deleted',
        });
        // check if item is in published list and remove it as well
        let regenerate;
        if (this.typeData.published) {
          const index = _.findIndex(this.typeData.published, p => p._id === this.activeItem._id);
          if (index >= 0) {
            regenerate = true;
            this.$store.dispatch('spliceDropletVal', {
              array: this.typeData.published,
              index,
            });
            this.$store.dispatch('setPublishItem', {
              type: this.activeSiteType,
              stamp: new Date().toISOString(),
            });
          }
        }
        await this.$lightning([{
          name: 'updateSiteAttr',
          payload: {
            siteId: this.currentSite._id,
            attr: 'dynamic',
            value: this.currentSite.dynamic,
          },
        }]);
        // we also need to regenerate the site in this instance
        if (regenerate) {
          await this.callBuilder({
            publish: 'site',
            site: {
              _id: this.currentSite._id,
            },
          });
        }
        this.$log.debug('successfully deleted item');
        this.$emit('set-active-item', null);
        this.$emit('set-content', null);
        this.$store.dispatch('addNotification', {
          id: this.guid(),
          type: 'success',
          time: 2000,
          message: 'Item deleted successfully!',
        });
      } catch (e) {
        this.$log.error(e);
        this.deleteErrors = e.message || e;
        await this._reportError({
          error: e,
          fn: 'deleteItem',
        });
      } finally {
        this.$emit('set-loading', false);
      }
    },
    async runPageChecks() {
      // if item has a pageId, delete the page
      if (this.activeItem.pageId) {
        const page = _.find(this.currentSite.pages, p => p._id === this.activeItem.pageId);
        if (!page) {
          return;
        }
        const canDelete = this._canDeletePage(page);
        if (canDelete) {
          // proceed to delete page
          // remove from sitemap state
          await this.$store.dispatch('removeFromSiteMap', page);
          // go delete the page folder in s3
          const path = this.buildPath(page.url);
          // build path for bucket
          const folder = `${this._customerFolder}/${this._sitePath}${path}`;
          // remove any counted droplets that are on this page
          this.removeCountedDroplets('forms', page);
          // remove page from state
          await this.$store.dispatch('removePage', page);
          const { _id, forms, siteMap } = this.currentSite;
          await this.$lightning([{
            name: 'deletePageItem',
            payload: {
              site: { _id, forms, siteMap },
              item: {
                _id: page._id,
                type: page.type,
              },
              folder,
            },
          }]);
          // if user is deleting the page they're on, set currentPage to homepage
          if (page._id === this.currentPage._id) {
            const homepage = _.find(this.currentSite.pages, {
              isHomepage: true,
            });
            const { item } = await this.checkQueuedWork({ page: homepage });
            this.$store.dispatch('setOriginalPage', item);
            this.$store.dispatch('setCurrentPage', item);
          }
          this.$log.debug('successfully deleted page: ', page.name);
        }
        if (!canDelete) {
          throw new Error('This item\'s page has 1 or more sub pages in the sitemap, you\'ll need to remove them before you can delete this. You can do this in the "global site options" window.');
        }
      }
    },
    setInnerTab(type) {
      _.forOwn(this.innerTabs, (value, key) => {
        if (key !== type) {
          this.innerTabs[key] = false;
        }
      });
      this.innerTabs[type] = true;
    },
    toggleConfirm(type) {
      this[type] = !this[type];
      const content = this.$refs[type];
      if (this[type]) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
    },
    setCropper(val, input) {
      this.activeInput = input;
      this.openCropper = val;
    },
    buildPath(url) {
      // Since the sitemap makes links take a depth, we need to make sure
      // it is properly doing so.
      let path = url;
      if (!path.endsWith('/') && path !== '/') {
        // remove last "/" if there
        path = `${path}/`;
      }
      if (!path.startsWith('/') && path !== '/') {
        // remove the first "/" if there
        path = `/${path}`;
      }
      if (path.trim() === '') {
        path = '/';
      }
      return path;
    },
    removeCountedDroplets(list, item) {
      if (item[list] && this.currentSite[list]) {
        _.forEach(item[list], (_id) => {
          const index = _.findIndex(this.currentSite[list], f => f === _id);
          if (index >= 0) {
            this.$store.dispatch('spliceDropletVal', {
              array: this.currentSite[list],
              index,
            });
          }
        });
      }
    },
    checkStyles(value, index) {
      const styles = {
        'grid-column-start': 1,
        'grid-column-end': 5,
        'grid-row-start': index + 1,
        'grid-row-end': index + 2,
      };
      if (value.grid) {
        styles['grid-column-start'] = value.grid.colStart;
        styles['grid-column-end'] = value.grid.colEnd;
        styles['grid-row-start'] = value.grid.rowStart;
        styles['grid-row-end'] = value.grid.rowEnd;
      }
      return styles;
    },
    lowerCase(string) {
      return _.lowerCase(string);
    },
    setInputError(input) {
      if (input === 'clear') {
        this.inputErrors = [];
        return;
      }
      const index = _.findIndex(this.inputErrors, inp => inp.key === input.key);
      const listed = index > -1;
      if (!listed && input.hasError) {
        this.inputErrors.push(input);
      }
      if (listed && !input.hasError) {
        this.inputErrors.splice(index, 1);
      }
    },
    checkAllRequired(auto) {
      this.inputErrors = [];
      this.showInputErrors = false;
      _.forOwn(this.dynamicData.itemData, (value, key) => {
        const itemVal = this.activeItem[key];
        if (value.required) {
          const emptyString = typeof itemVal === 'string' && itemVal.replace(/\s/g, '') === '';
          if (itemVal === false || emptyString) {
            this.setInputError({ key, hasError: true, tab: value.tab });
            this.showInputErrors = !auto;
          }
        }
        if (value.regex && itemVal) {
          const allowed = new RegExp(value.regex.value, 'g');
          if (!itemVal.match(allowed)) {
            this.setInputError({ key, hasError: true, tab: value.tab });
            this.showInputErrors = !auto;
          }
        }
      });
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/dynamic-data/edit-item/index.js